/**
 * Database and "Limit this connection to the selected database" (Option C, C2). Load lists the server's databases
 * (not Azure Data Explorer, which has no list); a loaded list turns the field into a native select while the listed
 * target is on screen (C0). The limit needs a database: scoping to "the selected database" with none selected would be
 * silently inert, so the box waits for one.
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InfoHint } from '@/components/ui/info-hint';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { DEFAULT_DATABASES } from '../../components/connectionFieldDefaults';
import { fieldApplies } from '../state/connectionFieldRules';
import { FormRow, rowLabelId } from '../pane/FormRow';
import { CHECK_ROW_CLASS, CHECKBOX_CLASS, SELECT_CLASS, type ConnectionFormApi } from './fieldControls';

type Props = {
  form: ConnectionFormApi;
  engine: DatabaseEngine;
  loadedDatabases: string[];
  onLoadDatabases?: () => void;
  loadingDatabases: boolean;
};

function LimitToDatabaseRow({ form }: { form: ConnectionFormApi }) {
  const { track } = useAnalytics();
  return (
    <form.Subscribe selector={(state: any) => state.values.database}>
      {(databaseValue: string | undefined) => {
        const hasDatabase = Boolean(String(databaseValue ?? '').trim());
        return (
          <form.Field name="limitToDatabase">
            {(field: any) => (
              <FormRow row="limitToDatabase">
                <div className={CHECK_ROW_CLASS}>
                  <input
                    id="limitToDatabase"
                    name="limitToDatabase"
                    type="checkbox"
                    className={CHECKBOX_CLASS}
                    checked={hasDatabase ? (field.state.value ?? false) : false}
                    disabled={!hasDatabase}
                    title={!hasDatabase ? 'Select a database first' : undefined}
                    onChange={(event) => field.handleChange(event.target.checked)}
                    onBlur={field.handleBlur}
                    data-testid="limit-to-database-checkbox"
                  />
                  <label htmlFor="limitToDatabase">Limit this connection to the selected database</label>
                  <InfoHint label="What does limiting the connection do?" onOpenByClick={() => track('feature:connection_limit_info')} data-testid="limit-to-database-info">
                    Scopes this connection inside Jam SQL Studio to the selected database: Object Explorer, database pickers,
                    and AI workspace export will only show and use it. It does not restrict SQL you write yourself (USE or
                    cross-database queries still work).
                  </InfoHint>
                  {!hasDatabase && <span className="text-xs text-muted-foreground" data-testid="limit-to-database-disabled-hint">Select a database first</span>}
                </div>
              </FormRow>
            )}
          </form.Field>
        );
      }}
    </form.Subscribe>
  );
}

export function DatabaseRows({ form, engine, loadedDatabases, onLoadDatabases, loadingDatabases }: Props) {
  if (!fieldApplies('database', { engine })) return null;
  const hasDatabases = loadedDatabases.length > 0;
  return (
    <>
      <form.Field name="database">
        {(field: any) => (
          <FormRow row="database" label="Database" htmlFor="database" note={hasDatabases ? `optional · ${loadedDatabases.length} available` : 'optional'}>
            <div className="flex items-center gap-2">
              {hasDatabases ? (
                <select
                  id="database"
                  name="database"
                  className={SELECT_CLASS}
                  value={field.state.value ?? ''}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={field.handleBlur}
                  data-testid="database-select"
                >
                  <option value="">(Default database)</option>
                  {loadedDatabases.map((db) => <option key={db} value={db}>{db}</option>)}
                </select>
              ) : (
                <Input
                  id="database"
                  name="database"
                  placeholder={DEFAULT_DATABASES[engine] || 'Default database'}
                  value={field.state.value ?? ''}
                  onChange={(event) => field.handleChange(event.target.value)}
                  onBlur={() => {
                    if (!field.state.value?.trim() && DEFAULT_DATABASES[engine]) field.handleChange(DEFAULT_DATABASES[engine]);
                    field.handleBlur();
                  }}
                  data-testid="database-input"
                />
              )}
              {onLoadDatabases && engine !== 'kusto' && (
                <Button type="button" variant="outline" onClick={onLoadDatabases} disabled={loadingDatabases} aria-describedby={rowLabelId('database')} data-testid="load-databases-button" className="whitespace-nowrap">
                  {loadingDatabases ? (
                    <>
                      <RandomDotmSpinner size={16} className="mr-1" />
                      Loading…
                    </>
                  ) : (
                    'Load'
                  )}
                </Button>
              )}
            </div>
          </FormRow>
        )}
      </form.Field>
      <LimitToDatabaseRow form={form} />
    </>
  );
}
